import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DOMPurify from "dompurify"; // Ochrana proti XSS
import TrainingSummaryPDF from "./TrainingSummaryPDF";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { ExerciseContext } from "../context/ExerciseContext";

const MAX_COMMENT_LENGTH = 500;

const SummaryScreen = () => {
    const navigate = useNavigate();
    const { selectedExercises } = useContext(ExerciseContext);
    const [rating, setRating] = useState(null);
    const [comment, setComment] = useState("");
    const location = useLocation();
    const { numSets } = location.state || { numSets: 1 };

    const validExercises = selectedExercises.filter(Boolean);
    const avgCardio =
        validExercises.length > 0
            ? validExercises.reduce((acc, ex) => acc + (ex.cardio || 0), 0) / validExercises.length
            : 0;
    const avgStrength =
        validExercises.length > 0
            ? validExercises.reduce((acc, ex) => acc + (ex.strength || 0), 0) / validExercises.length
            : 0;

    const getMotivationalMessage = () => {
        if (rating === "amazing") return "Skvělá práce! Překonal jsi sám sebe!";
        if (rating === "good") return "Super výkon! Každý trénink tě posouvá dál!";
        if (rating === "tough") return "To bylo náročné, ale zvládl jsi to! Respekt!";
        return "Skvělý výkon! Respekt!";
    };

    const sanitizedComment = DOMPurify.sanitize(comment);

    // Funkce pro sdílení
    const shareOnX = () => {
        const text = `Právě jsem dokončil svůj Tabata workout! 💪🔥\n\nVyzkoušej ho taky na https://tabataworkout.cz`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
    };

    const shareOnFacebook = () => {
        const url = encodeURIComponent("https://tabataworkout.cz");
        const quote = encodeURIComponent("Právě jsem dokončil svůj Tabata workout! 💪🔥 Vyzkoušej ho taky!");
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, "_blank");
    };

    const shareOnInstagram = () => {
        alert("Instagram bohužel neumožňuje sdílení přes URL. Můžeš si ale uložit obrázek nebo PDF a sdílet ručně.");
    };

    return (
        <div className="exercise-selector-container">
            {/* Pozadí */}
            <img src="/images/background.jpg" alt="background" className="bg-video" />
            <div className="overlay"></div>

            {/* Logo nahoře */}
            <div className="logo-container">
                <a href="https://www.tabataworkout.cz">
                    <img src="/images/logo.png" alt="Logo" className="logo" />
                </a>
            </div>

            {/* Shrnutí */}
            <div className="summary-container" style={{ textAlign: "center", padding: "20px" }}>
                <h2>{getMotivationalMessage()}</h2>

                <div style={{ marginTop: "20px" }}>
                    <h4>Odcvičené cviky</h4>
                    {selectedExercises.length > 0 ? (
                        <div>
                            {selectedExercises.map((exercise, index) => (
                                <p key={index}>{exercise?.name || "Neznámý cvik"}</p>
                            ))}
                        </div>
                    ) : (
                        <p>Žádné cviky nebyly odcvičeny.</p>
                    )}
                </div>

                <div style={{ marginTop: "20px" }}>
                    <h4>Chceš si něco poznamenat?</h4>
                    <textarea
                        value={comment}
                        onChange={(e) => {
                            if (e.target.value.length <= MAX_COMMENT_LENGTH) {
                                setComment(e.target.value);
                            }
                        }}
                        placeholder="Např. Cítil jsem se skvěle, ale poslední kolo bylo nejtěžší!"
                        style={{ width: "50%", height: "60px", padding: "10px" }}
                    />
                    <p>{comment.length}/{MAX_COMMENT_LENGTH} znaků</p>
                </div>

                <div style={{ marginTop: "30px" }}>
                    <h3>🔥 Připraven na další výzvu? 🔥</h3>
                </div>

                {/* Tlačítka a ikony */}
                <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "15px", flexWrap: "wrap" }}>
                    <PDFDownloadLink
                        document={
                            <TrainingSummaryPDF
                                exercises={selectedExercises}
                                feeling={rating || "Nezadáno"}
                                comment={sanitizedComment || "Bez komentáře"}
                                avgCardio={avgCardio}
                                avgStrength={avgStrength}
                                numSets={numSets}
                            />
                        }
                        fileName={`TabataWorkout-Trénink-${new Date().toISOString().slice(0,10)}.pdf`}
                        style={{
                            fontSize: "16px",
                            textDecoration: "none",
                            padding: "10px 20px",
                            backgroundColor: "#1c192c",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer"
                        }}
                    >
                        {({ loading }) => (loading ? "Generuji PDF..." : "📄 Stáhnout přehled tréninku v PDF")}
                    </PDFDownloadLink>

                    <button
                        onClick={() => navigate("/")}
                        style={{
                            fontSize: "16px",
                            padding: "10px 20px",
                            backgroundColor: "#E51937",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer"
                        }}
                    >
                        Spustit další Tabatu!
                    </button>

                    {/* Text před ikonou návratu */}
                    <span style={{ alignSelf: "center", fontSize: "16px" }}>Zpět na:</span>

                    {/* Ikona návratu na úvodní stránku */}
                    <a href="https://www.tabataworkout.cz" style={{ display: "inline-block", cursor: "pointer" }}>
                        <img src="/images/default.gif" alt="Home" style={{ width: "50px", height: "50px" }} />
                    </a>

                    {/* Text před sociálními ikonami */}
                    <span style={{ alignSelf: "center", fontSize: "16px" }}>Sdílet na:</span>

                    {/* Ikony sociálních sítí */}
                    <button onClick={shareOnFacebook} style={{ background: "none", border: "none", cursor: "pointer" }}>
                        <img src="/images/icon-facebook.png" alt="Facebook" style={{ width: "50px", height: "50px" }} />
                    </button>

                    <button onClick={shareOnInstagram} style={{ background: "none", border: "none", cursor: "pointer" }}>
                        <img src="/images/icon-instagram.png" alt="Instagram" style={{ width: "50px", height: "50px" }} />
                    </button>

                    <button onClick={shareOnX} style={{ background: "none", border: "none", cursor: "pointer" }}>
                        <img src="/images/icon-x.png" alt="X" style={{ width: "50px", height: "50px" }} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SummaryScreen;
